import { useMemo, useState } from "react";
import { View, TouchableOpacity, ScrollView } from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { Icon as RawIcon } from "@ant-design/react-native";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import { colorPalette } from "@shared/config/colorsConfig";
import { CenteredModal } from "@shared/components/CenteredModal";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { DEVICE_TUTORIAL_KEY, useDeviceTutorials, usePathwayId, TDeviceTutorial } from "app/_modules/pathways";
import { MediaViewer, isImageMediaUrl } from "@shared/components/MediaViewer";
import { remoteConfigManager } from "app/services/RemoteConfigManager";
import { FRONTEND_DEVICE_TUTORIAL_KEYS } from "../../constants/deviceTutorialConfig";
import { useOnboarding } from "app/_modules/onboarding";

const KNOWN_DEVICE_TUTORIAL_KEYS = new Set<string>(Object.values(DEVICE_TUTORIAL_KEY));

const FRONTEND_TUTORIALS: TDeviceTutorial[] = FRONTEND_DEVICE_TUTORIAL_KEYS.map((deviceKey) => ({
    deviceKey,
    mediaUrl: '',
}));

interface TutorialsModalProps {
    visible: boolean;
    onClose: () => void;
}

export const TutorialsModal = ({ visible, onClose }: TutorialsModalProps) => {
    const { t } = dynamicTranslations();
    const pathwayId = usePathwayId();
    const { data, isFetching, isError } = useDeviceTutorials(pathwayId, { enabled: visible });
    const recognizedTutorials = (data?.tutorials ?? []).filter((tutorial) =>
        KNOWN_DEVICE_TUTORIAL_KEYS.has(tutorial.deviceKey)
    );
    const tutorials = [...FRONTEND_TUTORIALS, ...recognizedTutorials];

    const [activeTutorial, setActiveTutorial] = useState<TDeviceTutorial | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const { replayableTours, replayTour } = useOnboarding();
    const appOverviewTour = replayableTours[0];

    const handleClose = () => {
        setActiveTutorial(null);
        setIsPlaying(false);
        onClose();
    };

    const handleReplay = (tourId: string) => {
        handleClose();
        replayTour(tourId);
    };

    const handleTutorialPress = (tutorial: TDeviceTutorial) => {
        if (tutorial.deviceKey === DEVICE_TUTORIAL_KEY.app && appOverviewTour) {
            handleReplay(appOverviewTour.id);
            return;
        }
        setActiveTutorial(tutorial);
    };

    const mediaUrl = useMemo(() => {
        if (!activeTutorial) return '';
        return activeTutorial.mediaUrl || remoteConfigManager.getString(`${activeTutorial.deviceKey}_tutorial`, '');
    }, [activeTutorial]);
    const isImage = !!mediaUrl && isImageMediaUrl(mediaUrl);
    const showMedia = !!mediaUrl && (isImage || isPlaying);

    return (
        <CenteredModal visible={visible} onClose={handleClose}>
            <View
                className={`w-full bg-white rounded-[22px] max-h-[85%] overflow-hidden border-4 border-white ${activeTutorial ? '' : 'px-7 pt-5'}`}
            >
                <TouchableOpacity
                    onPress={handleClose}
                    className="absolute right-3 top-3 w-12 h-12 rounded-full bg-white border border-gray-300 items-center justify-center z-10"
                >
                    <RawIcon name="close" size={18} color={colorPalette.gray[500]} />
                </TouchableOpacity>

                {activeTutorial ? (
                    <View className="w-full" style={{ height: 420 }}>
                        {showMedia ? (
                            <MediaViewer
                                mediaUrl={mediaUrl}
                                style={{ height: '100%' }}
                                resizeMode="contain"
                                controls={true}
                                paused={false}
                                repeat={false}
                            />
                        ) : (
                            <LinearGradient
                                colors={['#0C3963', '#072742']}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={{ flex: 1, padding: 20, justifyContent: 'space-between' }}
                            >
                                <View className="flex-1 items-center justify-center">
                                    {mediaUrl ? (
                                        <TouchableOpacity onPress={() => setIsPlaying(true)}>
                                            <Icon name="play-circle" size={64} color={colorPalette.white} />
                                        </TouchableOpacity>
                                    ) : (
                                        <Text className="text-white text-center">
                                            {t('screens.activities.tutorials.unavailable')}
                                        </Text>
                                    )}
                                </View>
                                <Text className="font-semibold text-base text-white">
                                    {t(`screens.activities.tutorials.list.${activeTutorial.deviceKey}`)}
                                </Text>
                            </LinearGradient>
                        )}
                    </View>
                ) : (
                    <>
                        <Head type="h2" className="mb-2 pr-14">{t('screens.activities.tutorials.title')}</Head>
                        <Text className="text-base text-black mb-5">{t('screens.activities.tutorials.subtitle')}</Text>

                        <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
                            {isFetching && (
                                <Text className="text-base text-gray-500">{t('commons.loading.generic')}</Text>
                            )}
                            {isError && (
                                <Text className="text-base text-warning-500">{t('screens.activities.tutorials.error')}</Text>
                            )}
                            <View className="flex-col gap-3">
                                {tutorials.map((tutorial) => (
                                    <TouchableOpacity
                                        key={tutorial.deviceKey}
                                        onPress={() => handleTutorialPress(tutorial)}
                                        className="flex-row items-center gap-4 border border-gray-300 rounded-2xl px-4 py-3"
                                    >
                                        <LinearGradient
                                            colors={['#0C3963', '#072742']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={{ width: 54, height: 40, borderRadius: 9, alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <Icon name="play-circle" size={22} color={colorPalette.white} />
                                        </LinearGradient>
                                        <View className="flex-1">
                                            <Text className="font-semibold text-base">
                                                {t(`screens.activities.tutorials.list.${tutorial.deviceKey}`)}
                                            </Text>
                                        </View>
                                        <Icon name="right" size={18} color={colorPalette.gray[500]} />
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </ScrollView>
                    </>
                )}
            </View>
        </CenteredModal>
    );
};

export default TutorialsModal;
